import { getTranslation } from 'helpers/getTranslation';

/*
 * Human wording for the technical parts of diagnostic journey events: what a
 * befront-api request means for the student, what its HTTP status means, what kind
 * of client crash happened and which control was rage-clicked. Raw endpoints,
 * statuses, error names and selectors stay in the developer view only.
 */

const readNonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const readFiniteNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** `null` when the key has no translation (i18next returns the key itself). */
const translateOptional = (
  key: string,
  params?: Record<string, unknown>,
): string | null => {
  const translated = getTranslation(key, params);

  return translated === key ? null : translated;
};

/** First key that has a translation; the last key is returned as is otherwise. */
const translateFirstAvailable = (
  keys: string[],
  params?: Record<string, unknown>,
): string => {
  for (const key of keys.slice(0, -1)) {
    const translated = translateOptional(key, params);

    if (translated) {
      return translated;
    }
  }

  return getTranslation(keys[keys.length - 1], params);
};

/* Endpoints */

const ORIGIN_PATTERN = /^[a-z][a-z0-9+.-]*:\/\/[^/]+/i;
const API_PREFIX_PATTERN = /^\/api\/v\d+(?=\/|$)/i;
const ID_SEGMENT_PATTERN =
  /^(?:[0-9a-f]{24}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)$/i;

/**
 * Route template as one canonical string: no origin, query or hash, and every
 * `:param` / ObjectId / uuid / number segment becomes `:id` — server events use
 * Nest names (`:lessonId`), clients use `:id`, so the same request looks the same.
 */
export const normalizeEndpointTemplate = (endpoint: string): string => {
  const path = endpoint.trim().replace(ORIGIN_PATTERN, '').split(/[?#]/)[0];
  const segments = path
    .split('/')
    .filter((segment) => segment.length > 0)
    .map((segment) =>
      segment.startsWith(':') || ID_SEGMENT_PATTERN.test(segment)
        ? ':id'
        : segment,
    );

  return `/${segments.join('/')}`;
};

type EndpointRule = {
  key: string;
  pattern: RegExp;
  /** Upper-case HTTP methods the rule applies to; any method when omitted. */
  methods?: ReadonlyArray<string>;
};

const READ_METHODS = ['GET', 'HEAD'];
const WRITE_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

/**
 * Ordered: the first matching rule wins, so specific routes go before their
 * prefixes. Patterns run against the normalized template without `/api/vN`.
 * Covers every befront-api controller route (src/features/*.controller.ts).
 */
const ENDPOINT_RULES: ReadonlyArray<EndpointRule> = [
  { key: 'otp_send', pattern: /^\/auth\/(?:send-code|resend-code)$/ },
  { key: 'otp_verify', pattern: /^\/auth\/verify(?:-google)?-code$/ },
  { key: 'check_email', pattern: /^\/auth\/check-email$/ },
  { key: 'register', pattern: /^\/auth\/(?:guest-)?register$/ },
  { key: 'refresh', pattern: /^\/auth\/refresh$/ },
  { key: 'logout', pattern: /^\/auth\/logout$/ },
  { key: 'promo_code', pattern: /^\/auth\/validate-promo-code$/ },
  { key: 'intro_practice', pattern: /^\/auth\/complete-start-lesson$/ },
  {
    key: 'streak',
    pattern: /^(?:\/auth\/me\/streak|\/student-activity\/streak)(?:\/|$)/,
  },
  { key: 'leaderboard', pattern: /^\/auth\/me\/leaderboard$/ },
  {
    key: 'activity_save',
    pattern: /^\/(?:auth\/me\/activity|student-activity)(?:\/|$)/,
    methods: WRITE_METHODS,
  },
  {
    key: 'activity',
    pattern: /^\/(?:auth\/me\/activity|student-activity)(?:\/|$)/,
  },
  { key: 'presence', pattern: /^\/auth\/me\/presence$/ },
  {
    key: 'mailing',
    pattern:
      /^\/(?:auth\/me\/(?:unsubscribe|subscribe)|student-unsubscribe-reasons)(?:\/|$)/,
  },
  {
    key: 'figma_progress_reset',
    pattern: /^\/auth\/me\/figma-lessons\/:id\/reset-task-progress$/,
  },
  {
    key: 'figma_progress_save',
    pattern: /^\/(?:auth\/me\/)?figma-lessons\/:id\/sync$/,
  },
  { key: 'lesson', pattern: /^\/auth\/me\/figma-lessons\/:id$/ },
  { key: 'profile_save', pattern: /^\/auth\/me$/, methods: WRITE_METHODS },
  { key: 'profile', pattern: /^\/auth\/me$/ },
  { key: 'figma_account', pattern: /^\/auth\/figma\/(?:link|unlink)$/ },
  {
    key: 'plugin_handoff',
    pattern:
      /^\/(?:auth\/figma\/(?:prepare-handoff|plugin-redirect-context)|plugin\/auth\/figma-handoff)$/,
  },
  /* Unknown sign-in routes: «Не сработало: вход», but «Не загрузилось: данные для входа». */
  { key: 'auth', pattern: /^\/auth(?:\/|$)/, methods: WRITE_METHODS },
  { key: 'auth_data', pattern: /^\/auth(?:\/|$)/ },

  { key: 'lesson_complete', pattern: /\/lessons\/:id\/complete$/ },
  { key: 'lesson_rate', pattern: /\/lessons\/:id\/rate$/ },
  { key: 'lesson_report', pattern: /\/lessons\/:id\/report$/ },
  { key: 'lesson_resources', pattern: /\/lessons\/:id\/resources(?:\/|$)/ },
  {
    key: 'task_check',
    pattern: /^\/figma-lessons\/:id\/(?:check|check-ref)$/,
  },
  { key: 'task_completed', pattern: /^\/analytics\/task-completed$/ },
  { key: 'analytics', pattern: /^\/analytics(?:\/|$)/ },
  { key: 'reference_categories', pattern: /^\/lessons\/categories$/ },
  { key: 'reference_lesson', pattern: /^\/lessons\/catalog\/:id$/ },
  { key: 'reference', pattern: /^\/lessons\/catalog$/ },
  {
    key: 'lesson',
    pattern:
      /^\/(?:courses|trainings|arcades|learning-path\/modules)\/:id\/lessons\/:id$/,
  },
  {
    key: 'lesson',
    pattern:
      /^\/(?:lessons|figma-lessons|theory-lessons|quizzes|connection-quizzes|two-columns-lessons|difference-lessons|colorMatch-lessons|color-match-lessons|chat-lessons)\/:id$/,
  },
  {
    key: 'lessons_list',
    pattern:
      /^\/(?:courses|trainings|arcades|learning-path\/modules)\/:id\/lessons$/,
  },
  { key: 'course_modules', pattern: /^\/courses\/:id\/modules$/ },

  {
    key: 'progress_reset',
    pattern:
      /^\/(?:learning-path\/modules|trainings|arcades|courses)\/:id\/reset-progress$/,
  },
  { key: 'favorite', pattern: /\/:id\/favorite$/ },
  { key: 'trainer_start', pattern: /^\/learning-path\/modules\/:id\/start$/ },
  { key: 'trainer', pattern: /^\/learning-path\/modules\/:id$/ },
  { key: 'learning_path', pattern: /^\/learning-paths?(?:\/|$)/ },
  { key: 'training_start', pattern: /^\/trainings\/:id\/start$/ },
  {
    key: 'trainings_list',
    pattern: /^\/trainings(?:\/(?:progress|favorite|featured))?$/,
  },
  { key: 'training', pattern: /^\/trainings\/:id$/ },
  { key: 'arcade_start', pattern: /^\/arcades\/:id\/start$/ },
  {
    key: 'arcades_list',
    pattern: /^\/arcades(?:\/(?:progress|favorite|featured))?$/,
  },
  { key: 'arcade', pattern: /^\/arcades\/:id$/ },
  { key: 'course_start', pattern: /^\/courses\/:id\/start$/ },
  {
    key: 'courses_list',
    pattern: /^\/courses(?:\/(?:progress|favorite|featured))?$/,
  },
  { key: 'course', pattern: /^\/courses\/(?:slug\/[^/]+|:id)$/ },
  { key: 'daily_challenge', pattern: /^\/daily-challenges(?:\/|$)/ },

  { key: 'subscription_cancel', pattern: /^\/payments\/cancel$/ },
  { key: 'billing_portal', pattern: /^\/payments\/billing-portal$/ },
  { key: 'pricing', pattern: /^\/payments\/config(?:\/|$)/ },
  { key: 'payment', pattern: /^\/(?:payments|checkout|subscriptions)(?:\/|$)/ },

  { key: 'certificates', pattern: /^\/certificates(?:\/|$)/ },
  {
    key: 'survey_submit',
    pattern: /^\/surveys(?:\/|$)/,
    methods: WRITE_METHODS,
  },
  { key: 'survey', pattern: /^\/surveys(?:\/|$)/ },
  { key: 'upload_remove', pattern: /^\/upload\/remove$/ },
  { key: 'upload', pattern: /^\/upload(?:\/|$)/ },
  { key: 'student_profile', pattern: /^\/students\/:id$/ },
  { key: 'school', pattern: /^\/(?:schools|school-codes)(?:\/|$)/ },
  { key: 'notifications', pattern: /^\/notifications(?:\/|$)/ },
  { key: 'referrals', pattern: /^\/referrals(?:\/|$)/ },
  { key: 'search', pattern: /^\/search(?:\/|$)/ },
  { key: 'figma_skills', pattern: /^\/figma-skills(?:\/|$)/ },
  { key: 'attribution', pattern: /^\/identity(?:\/|$)/ },
  { key: 'review', pattern: /^\/student-reviews(?:\/|$)/ },
  { key: 'demo_request', pattern: /^\/common\/demo-request$/ },
];

export const ENDPOINT_FALLBACK_KEY = 'request';

export type EndpointDescription = {
  /** Dictionary key (`student_page.timeline.endpoints.<key>`), stable for grouping. */
  key: string;
  /** What the request was for the student, e.g. «урок», «проверка задания». */
  label: string;
  /** True when no rule matched and `label` is the generic «запрос к серверу». */
  isFallback: boolean;
};

const translateEndpointKey = (key: string): string | null =>
  translateOptional(`student_page.timeline.endpoints.${key}`);

const fallbackEndpointDescription = (): EndpointDescription => ({
  key: ENDPOINT_FALLBACK_KEY,
  label: translateEndpointKey(ENDPOINT_FALLBACK_KEY) ?? ENDPOINT_FALLBACK_KEY,
  isFallback: true,
});

/** «адрес API → что это для студента» via the ordered route dictionary above. */
export const describeEndpoint = (
  method: string | null | undefined,
  endpoint: string | null | undefined,
): EndpointDescription => {
  const rawEndpoint = readNonEmptyString(endpoint);

  if (!rawEndpoint) {
    return fallbackEndpointDescription();
  }

  const upperMethod = readNonEmptyString(method)?.toUpperCase() ?? null;
  const path =
    normalizeEndpointTemplate(rawEndpoint).replace(API_PREFIX_PATTERN, '') ||
    '/';
  const rule = ENDPOINT_RULES.find(
    (candidate) =>
      candidate.pattern.test(path) &&
      (!candidate.methods ||
        (upperMethod != null && candidate.methods.includes(upperMethod))),
  );
  const label = rule ? translateEndpointKey(rule.key) : null;

  return rule && label
    ? { key: rule.key, label, isFallback: false }
    : fallbackEndpointDescription();
};

const isReadMethod = (method: string | null | undefined) => {
  const upperMethod = readNonEmptyString(method)?.toUpperCase();

  return !upperMethod || READ_METHODS.includes(upperMethod);
};

/**
 * The request as a circumstance for sentences: «при загрузке урока», «при
 * проверке задания», «при запросе к серверу» (unknown or missing endpoint).
 */
export const getEndpointWhen = (
  method: string | null | undefined,
  endpoint: string | null | undefined,
): string => {
  const { key } = describeEndpoint(method, endpoint);

  return translateFirstAvailable([
    `student_page.timeline.endpoints_when.${key}`,
    `student_page.timeline.endpoints_when.${ENDPOINT_FALLBACK_KEY}`,
  ]);
};

/* HTTP statuses */

export type HttpStatusKind =
  | 'server'
  | 'not_found'
  | 'forbidden'
  | 'timeout'
  | 'rate_limited'
  | 'rejected'
  | 'network'
  | 'client';

/** Class of a failed response (status wins over the client `error_code`). */
export const getHttpStatusKind = (
  status: number | null | undefined,
  errorCode?: string | null,
): HttpStatusKind | null => {
  const code = readFiniteNumber(status);

  if (code != null && code >= 400) {
    if (code >= 500) {
      return 'server';
    }

    if (code === 404) {
      return 'not_found';
    }

    if (code === 401 || code === 403) {
      return 'forbidden';
    }

    if (code === 408) {
      return 'timeout';
    }

    return code === 429 ? 'rate_limited' : 'rejected';
  }

  switch (readNonEmptyString(errorCode)) {
    case 'timeout':
      return 'timeout';
    case 'network':
      return 'network';
    case 'http_5xx':
      return 'server';
    case 'http_4xx':
      return 'client';
    default:
      return null;
  }
};

/** «ошибка на сервере (500)», «нет связи с сервером»… — `null` for success / unknown. */
export const getHttpStatusLabel = (
  status: number | null | undefined,
  errorCode?: string | null,
): string | null => {
  const kind = getHttpStatusKind(status, errorCode);

  if (!kind) {
    return null;
  }

  const code = readFiniteNumber(status);
  const prefix = 'student_page.timeline.http_statuses';

  if (code == null) {
    return (
      translateOptional(`${prefix}.${kind}_no_status`) ??
      translateOptional(`${prefix}.${kind}`)
    );
  }

  return translateOptional(`${prefix}.${kind}`, { status: code });
};

/**
 * Title of an API Error row: «Не загрузилось: урок» / «Не сработало: оплата»…
 * An unknown or missing endpoint never gets pasted in as «запрос к серверу»: the
 * row says «Не загрузились данные» / «Не сработало действие» instead.
 */
export const getApiErrorTitle = (properties?: Record<string, unknown>) => {
  const method = readNonEmptyString(properties?.method);
  const endpoint = readNonEmptyString(properties?.endpoint);
  const kind = getHttpStatusKind(
    readFiniteNumber(properties?.status),
    readNonEmptyString(properties?.error_code),
  );
  const variant =
    kind === 'rejected'
      ? 'api_error_rejected'
      : isReadMethod(method)
      ? 'api_error'
      : 'api_error_action';
  const description = describeEndpoint(method, endpoint);

  if (description.isFallback) {
    return translateFirstAvailable([
      `student_page.timeline.events.${variant}_generic`,
      'student_page.timeline.events.api_error_generic',
      'student_page.timeline.events.api_error',
    ]);
  }

  return getTranslation(`student_page.timeline.events.${variant}`, {
    what: description.label,
  });
};

/** Title of a Slow Request row: «Долго ждал: список тренажёров» / «Долго ждал ответа сервера». */
export const getSlowRequestTitle = (properties?: Record<string, unknown>) => {
  const description = describeEndpoint(
    readNonEmptyString(properties?.method),
    readNonEmptyString(properties?.endpoint),
  );

  return description.isFallback
    ? translateFirstAvailable([
        'student_page.timeline.events.slow_request_generic',
        'student_page.timeline.events.slow_request',
      ])
    : getTranslation('student_page.timeline.events.slow_request', {
        what: description.label,
      });
};

/* Client errors */

export type ClientErrorKindLabel =
  | 'chunk_load'
  | 'render'
  | 'main_thread'
  | 'background'
  | 'exception';

const CHUNK_LOAD_PATTERN =
  /loading (?:css )?chunk [\w-]+ failed|failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module/i;

/** Which humanised explanation fits a Client Error (from error_kind/fatal/thread/name). */
export const getClientErrorKindLabel = (
  properties?: Record<string, unknown>,
): ClientErrorKindLabel => {
  const errorName = readNonEmptyString(properties?.error_name);
  const message = readNonEmptyString(properties?.message);
  const errorKind = readNonEmptyString(properties?.error_kind);

  if (
    errorName === 'ChunkLoadError' ||
    (message != null && CHUNK_LOAD_PATTERN.test(message))
  ) {
    return 'chunk_load';
  }

  if (errorKind === 'main_thread' || properties?.thread === 'main') {
    return 'main_thread';
  }

  if (errorKind === 'render' || properties?.fatal === true) {
    return 'render';
  }

  return errorKind === 'unhandled_rejection' ? 'background' : 'exception';
};

/** «Сбой внутри плагина Figma · при загрузке урока», «Экран сломался — …». */
export const describeClientError = (
  properties?: Record<string, unknown>,
): string => {
  const kind = getClientErrorKindLabel(properties);
  const description = getTranslation(
    `student_page.timeline.client_error_kinds.${kind}`,
  );
  const messageType = readNonEmptyString(properties?.message_type);
  const step =
    kind === 'main_thread' && messageType
      ? translateOptional(
          `student_page.timeline.client_error_steps.${messageType.toUpperCase()}`,
        )
      : null;

  return step ? `${description} · ${step}` : description;
};

/**
 * The crash as a short clause for the middle of a sentence («…, после этого экран
 * сломался»): no explanation after a dash, the full one stays on the row.
 */
export const describeClientErrorBriefly = (
  properties?: Record<string, unknown>,
): string =>
  getTranslation(
    `student_page.timeline.client_error_kinds_short.${getClientErrorKindLabel(
      properties,
    )}`,
  );

export type JourneyPlatform = 'Web' | 'Plugin' | 'Backend' | string;

/** «Сбой в плагине» / «Сбой на сайте» (thread is plugin-only when the platform is unknown). */
export const getClientErrorTitle = (
  properties?: Record<string, unknown>,
  platform?: JourneyPlatform | null,
) => {
  const isPlugin =
    platform === 'Plugin' ||
    (platform !== 'Web' && readNonEmptyString(properties?.thread) != null);
  const variant = isPlugin
    ? 'client_error_plugin'
    : platform === 'Web'
    ? 'client_error_web'
    : 'client_error';

  return getTranslation(`student_page.timeline.events.${variant}`);
};

/* Rage clicks */

export type RageClickElement =
  | 'button'
  | 'link'
  | 'tab'
  | 'toggle'
  | 'field'
  | 'image'
  | 'element';

export type RageClickTarget = {
  /** Visible text or aria-label — what the student saw on the control. */
  label: string | null;
  element: RageClickElement;
};

/** `tag` + optional `#testid` / `[role=…]` / `[aria-label=…]` + optional `.text` (both clients). */
const RAGE_CLICK_TARGET_PATTERN =
  /^([a-z][a-z0-9-]*)(?:#[^.\s[]*|\[(role|aria-label)=([^\]]*)\]?)?(?:\.(.*))?$/i;

const TOGGLE_ROLES = new Set([
  'checkbox',
  'radio',
  'switch',
  'menuitemcheckbox',
  'menuitemradio',
]);

const ROLE_ELEMENTS: Record<string, RageClickElement> = {
  button: 'button',
  link: 'link',
  tab: 'tab',
};

/** The ARIA role wins over the tag (`a[role=tab]` is a tab for the student). */
const resolveRageClickElement = (
  tag: string,
  role: string | null,
): RageClickElement => {
  if (role != null && ROLE_ELEMENTS[role]) {
    return ROLE_ELEMENTS[role];
  }

  if (role != null && TOGGLE_ROLES.has(role)) {
    return 'toggle';
  }

  if (tag === 'button') {
    return 'button';
  }

  if (tag === 'a') {
    return 'link';
  }

  if (tag === 'input' || tag === 'textarea' || tag === 'select') {
    return 'field';
  }

  return ['img', 'svg', 'picture', 'canvas', 'video'].includes(tag)
    ? 'image'
    : 'element';
};

export const parseRageClickTarget = (target: string): RageClickTarget => {
  const match = RAGE_CLICK_TARGET_PATTERN.exec(target.trim());

  if (!match) {
    return { label: null, element: 'element' };
  }

  const [, tag, qualifier, qualifierValue, text] = match;
  const role =
    qualifier?.toLowerCase() === 'role'
      ? readNonEmptyString(qualifierValue)?.toLowerCase() ?? null
      : null;
  const ariaLabel =
    qualifier?.toLowerCase() === 'aria-label'
      ? readNonEmptyString(qualifierValue)
      : null;

  return {
    label: readNonEmptyString(text) ?? ariaLabel,
    element: resolveRageClickElement(tag.toLowerCase(), role),
  };
};

/** «Проверить» in quotes, or «на кнопку без подписи» when the control has no text. */
export const formatRageClickTarget = (target: string): string => {
  const { label, element } = parseRageClickTarget(target);

  return label
    ? `«${label}»`
    : getTranslation(`student_page.timeline.rage_click.elements.${element}`);
};

/** «Нажал «Проверить» 5 раз подряд» / «Нажал на кнопку без подписи 4 раза подряд». */
export const getRageClickTitle = (properties?: Record<string, unknown>) => {
  const target = readNonEmptyString(properties?.target);
  const clickCount = readFiniteNumber(properties?.click_count);

  if (!target) {
    return getTranslation('student_page.timeline.events.rage_click');
  }

  const { label, element } = parseRageClickTarget(target);
  const params = label
    ? { label }
    : {
        element: getTranslation(
          `student_page.timeline.rage_click.elements.${element}`,
        ),
      };
  const variant = label ? 'label' : 'element';

  return clickCount != null && clickCount > 0
    ? getTranslation(`student_page.timeline.rage_click.${variant}`, {
        ...params,
        count: clickCount,
      })
    : getTranslation(
        `student_page.timeline.rage_click.${variant}_no_count`,
        params,
      );
};
